import { useState } from 'react';
import { AlertTriangle, ShieldCheck, UserCog } from 'lucide-react';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';
import { usersApi, type UserWithDetails } from '../../services/api/endpoints/users';

interface TransferOwnershipModalProps {
  isOpen: boolean;
  onClose: () => void;
  users: UserWithDetails[];
  onSuccess: () => void;
}

export function TransferOwnershipModal({
  isOpen,
  onClose,
  users,
  onSuccess,
}: TransferOwnershipModalProps) {
  const [selectedUserId, setSelectedUserId] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [newRole, setNewRole] = useState<string>('accountant');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null);

  const selectedUser = users.find(u => u.id === selectedUserId);
  const isConfirmed = selectedUser && confirmEmail === selectedUser.email;

  const eligibleUsers = users.filter(u => u.role !== 'admin' && u.role !== 'super_admin');

  const handleClose = () => {
    setSelectedUserId('');
    setConfirmEmail('');
    setResult(null);
    onClose();
  };

  const handleTransfer = async () => {
    if (!isConfirmed || loading || !selectedUserId) return;

    setLoading(true);
    setResult(null);
    try {
      const response = await usersApi.transferOwnership({
        targetUserId: selectedUserId,
        confirmEmail,
        newRoleForCurrentAdmin: newRole,
      });

      if (response.success && response.data) {
        setResult({
          success: true,
          message: `Propriété transférée avec succès à ${response.data.newAdmin.email}`,
        });
        setTimeout(() => {
          handleClose();
          onSuccess();
        }, 2500);
      } else {
        setResult({ success: false, message: response.error || 'Erreur lors du transfert' });
      }
    } catch (error: any) {
      setResult({ success: false, message: error?.message || 'Erreur lors du transfert' });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Transférer la propriété">
      <div className="space-y-5">
        {/* Warning */}
        <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-destructive">
                Opération irréversible
              </p>
              <ul className="mt-2 text-sm text-destructive/80 list-disc list-inside space-y-1">
                <li>Vous perdrez vos droits d'administrateur</li>
                <li>L'utilisateur sélectionné deviendra le nouvel administrateur</li>
                <li>Vous ne pourrez plus annuler cette action</li>
                <li>Votre rôle sera changé en "{newRole}"</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Result */}
        {result && (
          <div className={`p-3 rounded-lg text-sm flex items-center gap-2 ${
            result.success
              ? 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400'
              : 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400'
          }`}>
            {result.success ? <ShieldCheck className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
            {result.message}
          </div>
        )}

        {!result?.success && (
          <>
            {/* User selection */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">
                Sélectionner le nouvel administrateur
              </label>
              <select
                value={selectedUserId}
                onChange={(e) => {
                  setSelectedUserId(e.target.value);
                  setConfirmEmail('');
                }}
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground text-sm"
              >
                <option value="">— Choisir un utilisateur —</option>
                {eligibleUsers.map(u => (
                  <option key={u.id} value={u.id}>
                    {u.firstName} {u.lastName} — {u.email} ({u.role})
                  </option>
                ))}
              </select>
            </div>

            {/* New role for current admin */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">
                Votre nouveau rôle après transfert
              </label>
              <select
                value={newRole}
                onChange={(e) => setNewRole(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground text-sm"
              >
                <option value="accountant">Comptable</option>
                <option value="manager">Gérant</option>
                <option value="analyst">Analyste</option>
                <option value="viewer">Observateur</option>
              </select>
            </div>

            {/* Email confirmation */}
            {selectedUser && (
              <div className="space-y-2">
                <p className="text-sm text-foreground">
                  Pour confirmer le transfert, tapez l'email de l'utilisateur cible{' '}
                  <code className="font-mono font-semibold bg-muted px-1.5 py-0.5 rounded text-destructive">
                    {selectedUser.email}
                  </code>{' '}
                  ci-dessous :
                </p>
                <input
                  type="text"
                  value={confirmEmail}
                  onChange={(e) => setConfirmEmail(e.target.value)}
                  placeholder={selectedUser.email}
                  autoComplete="off"
                  disabled={loading}
                  className="w-full px-3 py-2 border border-border rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-destructive bg-background text-foreground disabled:opacity-50"
                />
              </div>
            )}
          </>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="outline" onClick={handleClose} disabled={loading}>
            Annuler
          </Button>
          {!result?.success && (
            <Button
              variant="destructive"
              onClick={handleTransfer}
              disabled={!isConfirmed || loading}
            >
              <UserCog className="h-4 w-4 mr-2" />
              {loading ? 'Transfert...' : 'Transférer la propriété'}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}
